import { ArrowRightIcon } from 'lucide-react'
import type { components } from '@joyful/openapi/types.gen'
import { cn } from '@joyful/utils'
import type { DialogProps } from '../../../../core'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  TextLink,
} from '../../../../core'
import { Markdown } from '../../../components/markdown'
import { getAnnouncementLaunchLabel } from '../getAnnouncementLaunchLabel'
import { useAnnouncementsPath } from '../hooks/useAnnouncementsPath'

type Announcement = components['schemas']['AnnouncementRead']

export interface AnnouncementDetailDialogProps extends Omit<
  DialogProps,
  'children' | 'open'
> {
  announcement: Announcement | null
  onOpenChange: (open: boolean) => void
}

export function AnnouncementDetailDialog({
  announcement,
  ...dialogProps
}: AnnouncementDetailDialogProps) {
  const announcementsPath = useAnnouncementsPath()

  return (
    <Dialog open={announcement !== null} {...dialogProps}>
      <DialogContent
        dialogOverlayProps={{ variant: 'light' }}
        className={cn(announcement?.file_url ? 'max-w-2xl' : 'max-w-xl')}
      >
        {announcement !== null ? (
          <>
            <DialogHeader className="flex justify-between">
              <DialogTitle className="text-2xl text-balance">
                {announcement.title}
              </DialogTitle>
              <DialogDescription className="text-tremor-content">
                {getAnnouncementLaunchLabel({
                  launchDate: announcement.launch_date,
                  rolloutStrategy: announcement.schedule.strategy,
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="flex flex-col gap-y-4 pt-0">
              {announcement.file_url ? (
                announcement.file_mime_type?.startsWith('video/') ? (
                  <video
                    src={announcement.file_url}
                    controls
                    autoPlay
                    className="max-h-72 w-full rounded-lg border object-cover"
                  />
                ) : (
                  <img
                    src={announcement.file_url}
                    alt={announcement.title}
                    loading="lazy"
                    className="h-auto max-h-72 w-full rounded-lg object-contain"
                  />
                )
              ) : null}
              <Markdown size="lg">{announcement.description}</Markdown>

              {announcementsPath !== null ? (
                <span className="self-end">
                  <DialogClose asChild>
                    <TextLink
                      size="sm"
                      to={announcementsPath}
                      underlineVariant="hover"
                      endIcon={<ArrowRightIcon />}
                    >
                      {`View all announcements`}
                    </TextLink>
                  </DialogClose>
                </span>
              ) : null}
            </DialogBody>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
